import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormHelperText,
  TextFieldProps,
} from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Countries } from '../../constants';
import { TPTextField } from '../../components/Inputs';

interface SignupFormProps {
  errorMessage?: string;
  curriculum?: string;
  subject?: string;
  initialData?: Partial<SignupFormData>;
  onSubmit?: (data: SignupFormData) => void;
  resetErrorMessage: () => void;
}

interface SignupFormData {
  name: string;
  phone: string;
  email: string;
  countryCode: string;
}

const deviceTimeZone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Singapore';

let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === deviceTimeZone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const SignupForm: React.FC<SignupFormProps> = ({
  errorMessage,
  initialData,
  curriculum,
  subject,
  onSubmit,
  resetErrorMessage,
}) => {
  // State
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
  });

  let formTitle = 'Best 1:1';
  let formDescription = 'Transform your ';

  if (subject) {
    formTitle += ` ${subject}`;
    formDescription += ` ${subject}`;
  } else if (curriculum) {
    formTitle += ` ${curriculum}`;
    formDescription += ` ${curriculum}`;
  } else {
    formTitle += 'Tutition';
  }

  formDescription += 'preparation with our expert';

  if (subject) {
    formDescription += `${subject}`;
  }

  formDescription += 'live 1:1 tutoring.';

  useEffect(() => {
    if (formData.phone) {
      if (formData.phone.length < 9) {
        setPhoneError('Phone number should have at least 8 characters.');
      } else {
        setPhoneError('');
      }
    }
  }, [formData.phone]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: SignupFormData = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      countryCode: formData.phone.split(/\d/)[0] || '',
    };

    if (onSubmit) {
      onSubmit(submitData);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneNumberChange = (value: string) => {
    setFormData({
      ...formData,
      phone: value || '',
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 3,
        boxShadow: 6,
        padding: { xs: 4, md: 5 },
        maxWidth: 500,
        width: '100%',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: 'gray.900',
          mb: 2,
          fontSize: { xs: '1.75rem', md: '2rem' },
        }}
      >
        Best 1:1 {curriculum || 'Tuitions'}
      </Typography>
      <Typography variant="body1" sx={{ color: 'gray.600', mb: 3 }}>
        Transform your IGCSE Maths preparation with our expert IGCSE Maths live
        1:1 tutoring.
      </Typography>

      {errorMessage && (
        <Box
          sx={{
            backgroundColor: 'error.light',
            color: 'error.dark',
            padding: 2,
            borderRadius: 2,
            mb: 3,
            border: '1px solid',
            borderColor: 'error.main',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Name Input */}
          <TPTextField
            {...textFieldProps}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Name"
            required
            fullWidth
            label="Name"
          />

          {/* Phone Number Input */}
          <Box>
            <PhoneInput
              international
              defaultCountry={defaultCountryCode}
              placeholder="Enter phone number"
              name="phoneNumber"
              value={formData.phone}
              onChange={handlePhoneNumberChange}
              className="phone-number-input-custom-styles"
              style={{
                height: '52px',
                backgroundColor: '#F1F5F9',
              }}
              numberInputProps={{
                style: {
                  height: '52px',
                  borderColor: phoneError ? '#FF2B2D' : '#CBD5E1',
                  fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.4375em',
                },
              }}
            />
            {phoneError && (
              <FormHelperText error sx={{ ml: 2 }}>
                {phoneError}
              </FormHelperText>
            )}
          </Box>

          {/* Email Input */}
          <TPTextField
            {...textFieldProps}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email Address"
            required
            fullWidth
            label="Email"
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              background: 'linear-gradient(to right, #4A90E2, #9B59B6)',
              fontWeight: 'bold',
              py: 2,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 6,
              },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default SignupForm;
